// AllTradesHistory/AllTradesTable.jsx - With G-FULFILLED Column & XLSX Export & Adjusted P&L %
import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  COLORS,
  formatDateTime,
  formatPrice,
  formatPnL,
  formatDuration,
} from '../TradeHistory/constants';

const DEFAULT_SLIPPAGE_COST_PCT = 0.05;

const AllTradesTable = ({ trades }) => {
  const headers = [
    { key: 'Strategy', color: COLORS.text.muted },
    { key: 'G-Fulfilled', color: COLORS.purple },
    { key: 'Direction', color: COLORS.text.muted },
    { key: 'TF', color: COLORS.text.muted },
    { key: 'Hour', color: COLORS.text.muted },
    { key: 'Status', color: COLORS.text.muted },
    { key: 'Entry Time', color: COLORS.text.muted },
    { key: 'Entry', color: COLORS.text.muted },
    { key: 'Exit Time', color: COLORS.text.muted },
    { key: 'Exit', color: COLORS.text.muted },
    { key: 'Duration', color: COLORS.text.muted },
    { key: 'Adj P&L %', color: COLORS.warning },
  ];

  // Export to XLSX
  const handleExportXLSX = () => {
    if (!trades?.length) return;

    const exportData = trades.map((t, idx) => {
      const pnlPct = parseFloat(t.pnl_pct) || 0;
      const adjPnlPct = pnlPct - DEFAULT_SLIPPAGE_COST_PCT;
      return {
        '#': idx + 1,
        'Strategy': t.bulk_strategy_id || '',
        'G-Fulfilled': t.fulfilled_dependency || '',
        'Direction': t.direction || '',
        'Timeframe': t.timeframe || '',
        'Hour': t.trading_hour || '',
        'Status': t.status || '',
        'Entry Time': t.entry_time || '',
        'Entry Price': parseFloat(t.entry_price) || 0,
        'Exit Time': t.exit_time || '',
        'Exit Price': parseFloat(t.exit_price) || 0,
        'Duration': formatDuration(t.entry_time, t.exit_time) || '',
        'Raw P&L ($)': parseFloat(t.pnl) || 0,
        'Raw P&L %': pnlPct,
        'Adj P&L %': parseFloat(adjPnlPct.toFixed(4)),
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 30 },  // Strategy
      { wch: 25 },  // G-Fulfilled
      { wch: 10 },  // Direction
      { wch: 8 },   // Timeframe
      { wch: 8 },   // Hour
      { wch: 12 },  // Status
      { wch: 20 },  // Entry Time
      { wch: 12 },  // Entry Price
      { wch: 20 },  // Exit Time
      { wch: 12 },  // Exit Price
      { wch: 10 },  // Duration
      { wch: 12 },  // Raw P&L ($)
      { wch: 10 },  // Raw P&L %
      { wch: 10 },  // Adj P&L %
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trade Log');

    const filename = `trade-log-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Empty state
  if (!trades?.length) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 64, color: alpha(COLORS.text.muted, 0.3), mb: 2 }} />
        <Typography variant="h6" sx={{ color: COLORS.text.muted, mb: 0.5 }}>
          No trades found
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.6) }}>
          Adjust filters or date range to see trades
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Export Button */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}
      >
        <Tooltip title="Export to Excel (.xlsx)">
          <IconButton
            onClick={handleExportXLSX}
            size="small"
            sx={{
              bgcolor: alpha(COLORS.success, 0.1),
              '&:hover': { bgcolor: alpha(COLORS.success, 0.2) },
            }}
          >
            <DownloadIcon sx={{ color: COLORS.success, fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.key}
                  sx={{
                    bgcolor: COLORS.bg.tertiary,
                    color: header.color,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
                    py: 1.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {header.key === 'Adj P&L %' ? (
                    <Tooltip title={`Adjusted P&L % (after ${DEFAULT_SLIPPAGE_COST_PCT}% slippage per trade)`}>
                      <span>{header.key}</span>
                    </Tooltip>
                  ) : (
                    header.key
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {trades.map((trade, index) => {
              const pnlPct = parseFloat(trade.pnl_pct) || 0;
              const adjPnlPct = pnlPct - DEFAULT_SLIPPAGE_COST_PCT;
              const isWin = adjPnlPct >= 0;
              const pnlColor = trade.status === 'completed'
                ? (isWin ? COLORS.success : COLORS.danger)
                : COLORS.text.muted;
              const isLong = trade.direction === 'LONG';

              return (
                <TableRow
                  key={`${trade.bulk_strategy_id}-${trade.entry_time}-${index}`}
                  sx={{
                    '&:hover': { bgcolor: alpha(COLORS.primary, 0.03) },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {/* Strategy ID */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.primary,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={trade.bulk_strategy_id}
                    >
                      {trade.bulk_strategy_id}
                    </Typography>
                  </TableCell>

                  {/* G-FULFILLED */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    {trade.fulfilled_dependency ? (
                      <Tooltip title={`G-Strategy: ${trade.fulfilled_dependency}`}>
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: 12 }} />}
                          label={trade.fulfilled_dependency}
                          size="small"
                          sx={{
                            bgcolor: alpha(COLORS.purple, 0.15),
                            color: COLORS.purple,
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 22,
                            maxWidth: 160,
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '& .MuiChip-icon': { color: COLORS.purple },
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </TableCell>

                  {/* Direction */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      icon={
                        isLong
                          ? <TrendingUpIcon sx={{ fontSize: 14 }} />
                          : <TrendingDownIcon sx={{ fontSize: 14 }} />
                      }
                      label={trade.direction}
                      size="small"
                      sx={{
                        bgcolor: alpha(isLong ? COLORS.success : COLORS.danger, 0.15),
                        color: isLong ? COLORS.success : COLORS.danger,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: 22,
                        '& .MuiChip-icon': { color: isLong ? COLORS.success : COLORS.danger },
                      }}
                    />
                  </TableCell>

                  {/* Timeframe */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      label={trade.timeframe}
                      size="small"
                      sx={{
                        bgcolor: alpha(COLORS.warning, 0.1),
                        color: COLORS.warning,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  </TableCell>

                  {/* Trading Hour */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      label={trade.trading_hour || '—'}
                      size="small"
                      sx={{
                        bgcolor: alpha(COLORS.purple, 0.1),
                        color: COLORS.purple,
                        fontWeight: 600,
                        fontSize: '0.6rem',
                        height: 18,
                      }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      label={trade.status}
                      size="small"
                      sx={{
                        bgcolor: alpha(
                          trade.status === 'completed'
                            ? COLORS.success
                            : trade.status === 'open'
                            ? COLORS.warning
                            : COLORS.purple,
                          0.15
                        ),
                        color:
                          trade.status === 'completed'
                            ? COLORS.success
                            : trade.status === 'open'
                            ? COLORS.warning
                            : COLORS.purple,
                        fontWeight: 600,
                        fontSize: '0.6rem',
                        height: 20,
                        textTransform: 'uppercase',
                      }}
                    />
                  </TableCell>

                  {/* Entry Time */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.text.secondary,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatDateTime(trade.entry_time)}
                    </Typography>
                  </TableCell>

                  {/* Entry Price */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.success,
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                      }}
                    >
                      {formatPrice(trade.entry_price)}
                    </Typography>
                  </TableCell>

                  {/* Exit Time */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.text.secondary,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatDateTime(trade.exit_time)}
                    </Typography>
                  </TableCell>

                  {/* Exit Price */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.danger,
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                      }}
                    >
                      {formatPrice(trade.exit_price)}
                    </Typography>
                  </TableCell>

                  {/* Duration */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontSize: '0.8rem' }}>
                      {formatDuration(trade.entry_time, trade.exit_time)}
                    </Typography>
                  </TableCell>

                  {/* Adjusted P&L % */}
                  <TableCell sx={{ py: 1.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Tooltip title={`Raw: ${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(2)}% − ${DEFAULT_SLIPPAGE_COST_PCT}% slippage`}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: pnlColor,
                          fontWeight: 700,
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                        }}
                      >
                        {trade.status === 'completed'
                          ? `${adjPnlPct >= 0 ? '+' : ''}${adjPnlPct.toFixed(2)}%`
                          : '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AllTradesTable;
